import { filterIsRange } from './FilterRegistry';
import { satisfies } from '../binding/predicate';

const DEFAULT_SIZE = { xs: 12, md: 6 };

/** The engine field a filter type is rendered by, where the two differ. */
const FIELD_BY_TYPE = {
  month: { type: 'date', views: ['month', 'year'], format: 'MM/yyyy' },
};

/**
 * Compiles a filter layout into the fields the schema engine renders, seeded with what is applied.
 *
 * A layout entry is already in the engine's field shape - `key`, `type`, `label`, `size` and whatever
 * the type reads (`options`, `optionsUrl`, `contextParameters`, `multiple`) - so this fills in the
 * defaults and hands the applied value to the field as its own.
 *
 * A list the layout does not carry is fetched from the registry key in `optionsUrl`, which the
 * engine's own lookup handles: a layout cannot hold the branch's call outcomes, but it can hold the
 * name of the thing that answers for them.
 *
 * @param {Array} layout - The filters to show, in order.
 * @param {Object} options - Compilation options.
 * @param {Object} options.values - Currently applied filters, by key.
 *
 * @returns {Array}
 */
export const resolveFilterFields = (layout, { values = null } = {}) =>
  (layout ?? []).map((entry) => {
    const applied = values?.[entry.key];
    const field = {
      ...entry,
      type: entry.type ?? 'text',
      size: entry.size ?? DEFAULT_SIZE,
      ...(FIELD_BY_TYPE[entry.type] ?? {}),
    };

    return applied !== undefined && applied !== null
      ? { ...field, defaultValue: applied }
      : field;
  });

/**
 * Compiles a filter layout into the `standardFilters` the list pages take, so the panel and the
 * filter list cannot disagree about which keys exist.
 *
 * A span is applied as two terms rather than one, so those keys are left to `resolveRangeFilters`.
 *
 * @param {Array} layout - The filters to show, in order.
 *
 * @returns {Array}
 */
export const resolveStandardFilters = (layout) =>
  (layout ?? [])
    .filter(({ type }) => !filterIsRange(type))
    .map(({ key, operator, defaultValue }) => ({
      keyName: key,
      ...(operator ? { operator } : {}),
      ...(defaultValue !== undefined ? { defaultValue } : {}),
    }));

/**
 * Compiles a filter layout into the `rangeFilters` the list pages take: the keys whose value is the
 * two ends of a span, applied as a greater-than and a less-than term.
 *
 * `includeExtremes` decides whether the ends themselves match.
 *
 * @param {Array} layout - The filters to show, in order.
 *
 * @returns {Array}
 */
export const resolveRangeFilters = (layout) =>
  (layout ?? [])
    .filter(({ type }) => filterIsRange(type))
    .map(({ key, includeExtremes, defaultValue }) => ({
      keyName: key,
      ...(includeExtremes ? { includeExtremes } : {}),
      ...(defaultValue !== undefined ? { defaultValue } : {}),
    }));

/** The value a field contributes to the applied filters; an empty selection contributes none. */
const filterValue = (value) =>
  Array.isArray(value) && value.length === 0 ? null : (value ?? null);

/**
 * The filters a panel applies, read off the form store by the keys the layout declares.
 *
 * Only declared keys are read: the store also carries its own markers, which are not filters.
 *
 * A filter the panel is not showing applies nothing, so a value left behind by a `visibleWhen` that
 * has since turned cannot go on filtering unseen. It is cleared rather than left out, which is how
 * the panel says a filter is off.
 *
 * @param {Array} layout - The filters shown.
 * @param {Object} values - The panel's branch of the form store.
 * @param {Object} context - The runtime values the page publishes, for the predicates naming them.
 *
 * @returns {Object}
 */
export const collectFilterValues = (layout, values, context = {}) => {
  const scopes = { form: values, record: null, context };

  return Object.fromEntries(
    (layout ?? []).map(({ key, visibleWhen }) => [
      key,
      satisfies(visibleWhen, scopes) ? filterValue(values?.[key]) : null,
    ])
  );
};
